/* eslint-disable no-console */
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  guardarOrdenMedio,
  vincularOrdenMedioExistente,
  reemplazarOrdenMedio,
  subirArchivoOrdenMedio,
  actualizarArchivoOrdenMedio,
  getOrdenMedioVigentePorNumero,
  getVinculosVigentesDeOrdenMedio,
  sumarNetoVinculosExistentes,
  archivoPendientePlaceholder,
  TOLERANCIA_OM_PCT,
} from "../../utils/ordenesMedio";
import { useCurrency } from "../../utils/useCurrency";

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const claveSibling = (s) => `${s.contratoId}:${s.mes}`;

/**
 * Modal "Cargar OM" / "Reemplazar OM" (Fase OM.1b — completa la spec original de OM.1).
 *
 * `data` (armado por VentasDetalle, ver `construirCargarOmData`):
 *  - pais, modo: 'cargar' | 'reemplazar'
 *  - omVigente: null (modo cargar) | { omId, numeroOm, fechaOm, importeNeto, moneda, archivoPath,
 *      archivoNombreOriginal } (modo reemplazar)
 *  - basesFijas: [{ origenTipo, saleId?, contratoId?, mes?, etiquetaCliente, etiquetaAgencia,
 *      etiquetaMes?, neto, moneda }]
 *      — SIEMPRE incluidas en el guardado, no se pueden destildar (el target desde cuyo kebab se
 *      abrió el modal; en "reemplazar", TODOS los vínculos vigentes de la OM vieja — se re-vinculan
 *      todos, nunca un subconjunto, spec punto 3).
 *  - siblings: [{ contratoId, mes, etiquetaCliente, etiquetaAgencia, etiquetaMes, neto, moneda }]
 *      — otras cuotas SIN OM del mismo contrato, para tildar (spec punto 4).
 *  - oportunidadesTodas, contratosTodos: arrays completos ya cargados por VentasDetalle (para
 *      resolver el neto de los vínculos que una OM detectada por número YA tenía, punto 5).
 *
 * Flujo de guardado "RPC primero" (igual que OM.1): el `archivo_path` real necesita el `id` de la
 * fila, recién disponible después del INSERT — el PDF se sube DESPUÉS, con el id real. Si falla el
 * paso de subir/apuntar el archivo DESPUÉS de que la fila ya se creó, no hay DELETE (append-only) —
 * se avisa explícito con el id interno (10.177).
 */
export const CargarOrdenMedioModal = ({ show, onHide, data, onSaved }) => {
  const { formatAmount } = useCurrency();
  const monedaLocal = data?.pais === "Perú" ? "PEN" : "CLP";

  const [numeroOm, setNumeroOm] = useState("");
  const [fechaOm, setFechaOm] = useState(hoyISO());
  const [importeNeto, setImporteNeto] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [siblingsSeleccionados, setSiblingsSeleccionados] = useState(new Set());

  // Punto 5 — detección en vivo de "numero_om ya vigente" (solo en modo 'cargar', ver header).
  const [buscandoOm, setBuscandoOm] = useState(false);
  const [omDetectada, setOmDetectada] = useState(null);
  const [vinculandoExistente, setVinculandoExistente] = useState(false);
  const [netoExistenteOmDetectada, setNetoExistenteOmDetectada] = useState(0);

  const modo = data?.modo || "cargar";
  const basesFijas = useMemo(() => data?.basesFijas || [], [data]);
  const siblings = useMemo(() => data?.siblings || [], [data]);

  useEffect(() => {
    if (!show) return;
    setNumeroOm(modo === "reemplazar" ? data?.omVigente?.numeroOm || "" : "");
    setFechaOm(hoyISO());
    setImporteNeto("");
    setArchivo(null);
    setSiblingsSeleccionados(new Set());
    setOmDetectada(null);
    setVinculandoExistente(false);
    setNetoExistenteOmDetectada(0);
  }, [show, modo, data?.omVigente?.numeroOm]);

  // Punto 5: detectar OM vigente con el mismo número al tipear (debounce 400ms) — solo en 'cargar'.
  useEffect(() => {
    if (!show || modo !== "cargar") return undefined;
    if (!numeroOm.trim()) {
      setOmDetectada(null);
      setVinculandoExistente(false);
      return undefined;
    }
    setBuscandoOm(true);
    const timer = setTimeout(async () => {
      try {
        const encontrada = await getOrdenMedioVigentePorNumero({ pais: data.pais, numeroOm });
        setOmDetectada(encontrada);
        if (!encontrada) setVinculandoExistente(false);
      } catch (error) {
        console.error("Error buscando OM existente por número:", error);
      } finally {
        setBuscandoOm(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [numeroOm, modo, show, data?.pais]);

  // Al optar por "vincular a la existente": neto ya cubierto por esa OM, para la diferencia en vivo.
  useEffect(() => {
    if (!vinculandoExistente || !omDetectada) {
      setNetoExistenteOmDetectada(0);
      return;
    }
    (async () => {
      try {
        const vinculosExistentes = await getVinculosVigentesDeOrdenMedio(omDetectada.omId);
        setNetoExistenteOmDetectada(
          sumarNetoVinculosExistentes(vinculosExistentes, {
            oportunidades: data?.oportunidadesTodas || [],
            contratos: data?.contratosTodos || [],
          })
        );
      } catch (error) {
        console.error("Error calculando el neto ya vinculado de la OM detectada:", error);
      }
    })();
  }, [vinculandoExistente, omDetectada, data?.oportunidadesTodas, data?.contratosTodos]);

  const toggleSibling = (s) => {
    setSiblingsSeleccionados((prev) => {
      const next = new Set(prev);
      const k = claveSibling(s);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const targetsActivos = useMemo(() => {
    const elegidos = siblings.filter((s) => siblingsSeleccionados.has(claveSibling(s)));
    return [...basesFijas, ...elegidos];
  }, [basesFijas, siblings, siblingsSeleccionados]);

  const monedaDerivada = vinculandoExistente ? omDetectada?.moneda : (basesFijas[0]?.moneda ?? null);
  const monedaLabel = monedaDerivada === "USD" ? "USD" : monedaLocal;

  const netoTotalTargets = targetsActivos.reduce((t, x) => t + (x.neto || 0), 0);
  const hayMonedaMixta = targetsActivos.some((x) => (x.moneda || null) !== (monedaDerivada || null));

  const importeComparar = vinculandoExistente ? omDetectada?.importeNeto || 0 : Number(importeNeto) || 0;
  const netoComparativoTotal = (vinculandoExistente ? netoExistenteOmDetectada : 0) + netoTotalTargets;
  const diffMonto = importeComparar - netoComparativoTotal;
  const diffPct = netoComparativoTotal > 0 ? (Math.abs(diffMonto) / netoComparativoTotal) * 100 : null;
  const fueraDeTolerancia = !hayMonedaMixta && diffPct !== null && diffPct > TOLERANCIA_OM_PCT;

  const handleArchivoChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file && file.type !== "application/pdf") {
      toast.error("La Orden de Medio tiene que ser un PDF");
      e.target.value = "";
      setArchivo(null);
      return;
    }
    setArchivo(file);
  };

  const guardarDeshabilitado =
    saving ||
    targetsActivos.length === 0 ||
    !numeroOm.trim() ||
    !fechaOm ||
    (!vinculandoExistente && (!importeNeto || Number(importeNeto) <= 0)) ||
    (!vinculandoExistente && !archivo) ||
    fueraDeTolerancia;

  const handleSave = async () => {
    if (guardarDeshabilitado) return;
    const numero = numeroOm.trim();
    const vinculosPayload = targetsActivos.map((t) => ({
      origen_tipo: t.origenTipo,
      sale_id: t.origenTipo === "sale" ? t.saleId : null,
      contrato_id: t.origenTipo === "contrato_mes" ? t.contratoId : null,
      mes: t.origenTipo === "contrato_mes" ? t.mes : null,
    }));

    setSaving(true);

    // Punto 5: vincular a la OM existente — no crea fila ni sube archivo (reusa el PDF ya cargado).
    if (vinculandoExistente) {
      try {
        await vincularOrdenMedioExistente({ ordenMedioId: omDetectada.omId, vinculos: vinculosPayload });
        toast.success(`Venta/cuota vinculada a la OM ${omDetectada.numeroOm}`);
        setSaving(false);
        onSaved?.();
        onHide();
      } catch (error) {
        console.error("Error en vincularOrdenMedioExistente:", error);
        toast.error(error.message || "No se pudo vincular a la OM existente");
        setSaving(false);
      }
      return;
    }

    let om;
    try {
      const params = {
        numeroOm: numero,
        fechaOm,
        importeNeto: Number(importeNeto),
        moneda: monedaLabel,
        archivoPath: archivoPendientePlaceholder(data.pais),
        archivoNombreOriginal: archivo.name,
        vinculos: vinculosPayload,
      };
      om =
        modo === "reemplazar"
          ? await reemplazarOrdenMedio({ ordenMedioViejaId: data.omVigente.omId, ...params })
          : await guardarOrdenMedio({ pais: data.pais, ...params });
    } catch (error) {
      console.error("Error guardando/reemplazando la OM:", error);
      toast.error(error.message || "No se pudo guardar la Orden de Medio");
      setSaving(false);
      return;
    }

    try {
      const archivoPath = await subirArchivoOrdenMedio({ pais: data.pais, ordenMedioId: om.id, file: archivo });
      await actualizarArchivoOrdenMedio({ id: om.id, archivoPath, archivoNombreOriginal: archivo.name });
    } catch (error) {
      console.error(`Error subiendo/actualizando el archivo de la OM (fila ya creada, id ${om.id}):`, error);
      toast.error(
        `La OM ${numero} se guardó pero el PDF no se pudo adjuntar — usá "Adjuntar PDF" en el kebab de esta fila para reintentar (id interno ${om.id}).`,
        { duration: 8000 }
      );
      setSaving(false);
      onSaved?.();
      onHide();
      return;
    }

    toast.success(modo === "reemplazar" ? `OM reemplazada por ${numero}` : `Orden de Medio ${numero} guardada`);
    setSaving(false);
    onSaved?.();
    onHide();
  };

  if (!data) return null;

  const filaTarget = (t, conCheckbox) => (
    <div
      key={conCheckbox ? claveSibling(t) : `${t.origenTipo}-${t.saleId || t.contratoId}-${t.mes || ""}`}
      className="d-flex align-items-center gap-2 py-1 border-bottom"
      style={{ fontSize: "0.85rem" }}
    >
      {conCheckbox && (
        <Form.Check
          type="checkbox"
          checked={siblingsSeleccionados.has(claveSibling(t))}
          onChange={() => toggleSibling(t)}
        />
      )}
      <span className="flex-grow-1">{t.etiquetaCliente}</span>
      <span className="text-muted">{t.etiquetaAgencia || "-"}</span>
      <span>{t.etiquetaMes || "venta"}</span>
      <span style={{ minWidth: 110, textAlign: "right" }}>
        {formatAmount(t.neto, t.moneda === "USD" ? "USD" : monedaLocal)}
      </span>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {modo === "reemplazar" ? "Reemplazar Orden de Medio" : "Cargar Orden de Medio"}
          {data.etiqueta ? ` — ${data.etiqueta}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Punto 1: cliente/agencia/mes/neto facturable en solo lectura, de cada target activo. */}
        <div className="mb-2 p-2 bg-light rounded">
          <div className="d-flex gap-2 fw-bold text-muted" style={{ fontSize: "0.75rem" }}>
            <span className="flex-grow-1">CLIENTE</span>
            <span>AGENCIA</span>
            <span>MES</span>
            <span style={{ minWidth: 110, textAlign: "right" }}>NETO FACTURABLE</span>
          </div>
          {basesFijas.map((t) => filaTarget(t, false))}
          {siblings.length > 0 && (
            <>
              <div className="text-muted mt-2 mb-1" style={{ fontSize: "0.75rem" }}>
                Otras cuotas sin OM del mismo contrato (opcional, misma OM):
              </div>
              {siblings.map((s) => filaTarget(s, true))}
            </>
          )}
          <div className="d-flex justify-content-end fw-bold pt-1" style={{ fontSize: "0.85rem" }}>
            Total: {formatAmount(netoTotalTargets, monedaDerivada === "USD" ? "USD" : monedaLocal)}
            {hayMonedaMixta && <span className="text-warning ms-2">(monedas distintas entre targets)</span>}
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Número de OM</Form.Label>
          <Form.Control
            type="text"
            value={numeroOm}
            onChange={(e) => setNumeroOm(e.target.value)}
            disabled={saving}
            autoFocus
          />
          {modo === "cargar" && buscandoOm && <small className="text-muted">Buscando OM existente...</small>}
          {modo === "cargar" && omDetectada && !vinculandoExistente && (
            <div className="mt-2 p-2 rounded" style={{ backgroundColor: "#fff3cd", fontSize: "0.85rem" }}>
              Ya existe una OM vigente <strong>{omDetectada.numeroOm}</strong> por{" "}
              <strong>{formatAmount(omDetectada.importeNeto, omDetectada.moneda)}</strong> en {data.pais}.
              <div className="mt-1">
                <Button size="sm" variant="warning" onClick={() => setVinculandoExistente(true)}>
                  Vincular a esta OM existente
                </Button>
              </div>
            </div>
          )}
          {vinculandoExistente && (
            <div
              className="mt-2 p-2 rounded d-flex justify-content-between align-items-center"
              style={{ backgroundColor: "#d1e7dd", fontSize: "0.85rem" }}
            >
              <span>
                Vinculando a la OM existente <strong>{omDetectada?.numeroOm}</strong> (sin subir archivo nuevo).
              </span>
              <Button size="sm" variant="outline-secondary" onClick={() => setVinculandoExistente(false)}>
                Cancelar
              </Button>
            </div>
          )}
        </Form.Group>

        {!vinculandoExistente && (
          <Form.Group className="mb-3">
            <Form.Label>Fecha de la OM</Form.Label>
            <Form.Control type="date" value={fechaOm} onChange={(e) => setFechaOm(e.target.value)} disabled={saving} />
          </Form.Group>
        )}

        <div className="d-flex gap-2 mb-2">
          <Form.Group className="flex-grow-1">
            <Form.Label>Importe neto</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={vinculandoExistente ? (omDetectada?.importeNeto ?? "") : importeNeto}
              onChange={(e) => setImporteNeto(e.target.value)}
              disabled={saving || vinculandoExistente}
            />
          </Form.Group>
          <Form.Group style={{ width: "120px" }}>
            <Form.Label>Moneda</Form.Label>
            <Form.Control type="text" value={monedaLabel} disabled readOnly />
          </Form.Group>
        </div>

        {netoComparativoTotal > 0 && (
          <div
            className="mb-3 p-2 rounded"
            style={{
              backgroundColor: fueraDeTolerancia ? "#f8d7da" : "#d1e7dd",
              color: fueraDeTolerancia ? "#842029" : "#0f5132",
              fontSize: "0.85rem",
            }}
          >
            Diferencia: {formatAmount(diffMonto, monedaDerivada === "USD" ? "USD" : monedaLocal)}
            {diffPct !== null && ` (${diffPct.toFixed(2)}%)`} — tolerancia {TOLERANCIA_OM_PCT}%
            {hayMonedaMixta && " (monedas distintas, el servidor valida)"}
            {fueraDeTolerancia && " — fuera de tolerancia, no se puede guardar"}
          </div>
        )}

        {!vinculandoExistente && (
          <Form.Group className="mb-2">
            <Form.Label>PDF de la OM</Form.Label>
            <Form.Control type="file" accept="application/pdf" onChange={handleArchivoChange} disabled={saving} />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} size="sm" disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={guardarDeshabilitado} size="sm">
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
